/**
 * The pricing-sheet agent's reading of a workbook, rendered wherever the backend
 * returns a `gridAgent {recognition, validation}` block — the import response,
 * the diff response, and the on-demand agent review.
 *
 * WHAT THIS IS: a SECOND, independent reading of the same workbook, shown so a
 * layout change is caught at upload rather than discovered later as a silent
 * pricing gap. It is NOT the payload. The deterministic parse is what gets
 * stored and what prices loans; the agent's recognition genuinely varies between
 * runs (it sometimes merges the two LLPA tables — 7 grids one run, 8 the next),
 * which is exactly why the backend reports parser-vs-agent AGREEMENT rather than
 * letting either side win. A disagreement means "go look at the workbook".
 *
 * Renders nothing when the agent didn't run (it's optional server-side — no
 * model configured means no block), so it is safe to drop in unconditionally.
 */
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { A } from "../shared/adminTokens";
import { T } from "./pricingUi";

const GRID_LABELS = {
  llpa_table: "LLPA tables",
  coupon_ladder: "Coupon ladders",
  tier_margins: "Tier margins",
  min_max_prices: "Min / max prices",
};

export default function GridAgentReport({ gridAgent, compact }) {
  if (!gridAgent) return null;

  const warnings = gridAgent.validation?.warnings || [];
  const agreement = gridAgent.validation?.agreement || [];
  if (!warnings.length && !agreement.length) return null;

  return (
    <Box
      sx={{
        border: `1px solid ${warnings.length ? T.warnLine : T.line}`,
        bgcolor: warnings.length ? T.warnSoft : T.surface2,
        borderRadius: "10px",
        p: 1.75,
      }}
    >
      <Flx ac g={1} sx={{ mb: warnings.length || !compact ? 1.25 : 0 }}>
        {warnings.length ? (
          <WarningAmberRounded sx={{ fontSize: 18, color: T.warn }} />
        ) : (
          <CheckCircleOutlined sx={{ fontSize: 18, color: A.ok }} />
        )}
        <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>
          {warnings.length
            ? `Agent review — ${warnings.length} thing${warnings.length === 1 ? "" : "s"} to check`
            : "Agent review — parser and agent agree"}
        </Txt>
      </Flx>

      {warnings.length ? (
        <Flx column g={0.75} sx={{ mb: compact ? 0 : 1.25 }}>
          {warnings.map((w, i) => (
            <Txt
              key={i}
              sx={{ fontSize: 12, color: T.ink2, lineHeight: 1.5, display: "block" }}
            >
              • {w}
            </Txt>
          ))}
        </Flx>
      ) : null}

      {compact || !agreement.length ? null : (
        <Flx ac g={1.5} wrap>
          {agreement.map((row) => {
            const disagrees = row.parser !== row.agent;
            return (
              <Txt
                key={row.gridType}
                sx={{
                  fontSize: 11.5,
                  fontFamily: T.mono,
                  color: disagrees ? T.warn : T.muted,
                }}
              >
                {GRID_LABELS[row.gridType] || row.gridType}: {row.parser}/
                {row.agent}
              </Txt>
            );
          })}
          <Txt sx={{ fontSize: 11, color: T.faint }}>
            (parser/agent — the parser&apos;s reading is what gets saved)
          </Txt>
        </Flx>
      )}
    </Box>
  );
}

GridAgentReport.propTypes = {
  gridAgent: PropTypes.object,
  // Warnings only — for a dialog that already has a lot going on.
  compact: PropTypes.bool,
};
